import { useMutation, useQueryClient, queryOptions, keepPreviousData } from '@tanstack/react-query'
import { apiFetch, withQuery } from './http'
import { qk } from './queryKeys'
import type {
  Paged, RangeParams, Session, Site, SiteContact, SiteContactRequest, SiteFile, SiteFileCategory, SiteFileKind, SiteLive, SiteConditions, SiteLivePositions,
  SiteNotificationRecipient, SiteNotifications, SiteRequest,
} from './types'

export const sitesApi = {
  list: (p: { active?: boolean } = {}, signal?: AbortSignal) => apiFetch<Site[]>(withQuery('/sites', p), { signal }),
  get: (id: string, signal?: AbortSignal) => apiFetch<Site>(`/sites/${id}`, { signal }),
  create: (b: SiteRequest) => apiFetch<Site>('/sites', { method: 'POST', body: b }),
  update: (id: string, b: SiteRequest) => apiFetch<Site>(`/sites/${id}`, { method: 'PUT', body: b }),
  remove: (id: string) => apiFetch<void>(`/sites/${id}`, { method: 'DELETE' }), // soft delete => active=false
  live: (id: string, signal?: AbortSignal) => apiFetch<SiteLive>(`/sites/${id}/live`, { signal }),
  livePositions: (id: string, signal?: AbortSignal) => apiFetch<SiteLivePositions>(`/sites/${id}/live-positions`, { signal }),
  conditions: (id: string, signal?: AbortSignal) => apiFetch<SiteConditions>(`/sites/${id}/conditions`, { signal }),
  history: (id: string, p: RangeParams, signal?: AbortSignal) => apiFetch<Paged<Session>>(withQuery(`/sites/${id}/history`, p), { signal }),
  files: (id: string, kind: SiteFileKind, signal?: AbortSignal) => apiFetch<SiteFile[]>(withQuery(`/sites/${id}/files`, { kind }), { signal }),
  uploadFile: (id: string, u: SiteFileUpload) => {
    const form = new FormData()
    form.append('file', u.file, u.fileName ?? (u.file instanceof File ? u.file.name : 'upload'))
    form.append('kind', u.kind)
    if (u.category) form.append('category', u.category)
    if (u.caption?.trim()) form.append('caption', u.caption.trim())
    if (u.width) form.append('width', String(u.width))
    if (u.height) form.append('height', String(u.height))
    return apiFetch<SiteFile>(`/sites/${id}/files`, { method: 'POST', body: form })
  },
  downloadFile: (id: string, fileId: string, signal?: AbortSignal) => apiFetch<Blob>(`/sites/${id}/files/${fileId}`, { signal, as: 'blob' }),
  updateCaption: (id: string, fileId: string, caption: string, category?: SiteFileCategory) =>
    apiFetch<SiteFile>(`/sites/${id}/files/${fileId}`, { method: 'PUT', body: { caption, category } }),
  deleteFile: (id: string, fileId: string) => apiFetch<void>(`/sites/${id}/files/${fileId}`, { method: 'DELETE' }),
  contacts: (id: string, signal?: AbortSignal) => apiFetch<SiteContact[]>(`/sites/${id}/contacts`, { signal }),
  createContact: (id: string, b: SiteContactRequest) => apiFetch<SiteContact>(`/sites/${id}/contacts`, { method: 'POST', body: b }),
  updateContact: (id: string, contactId: string, b: SiteContactRequest) => apiFetch<SiteContact>(`/sites/${id}/contacts/${contactId}`, { method: 'PUT', body: b }),
  deleteContact: (id: string, contactId: string) => apiFetch<void>(`/sites/${id}/contacts/${contactId}`, { method: 'DELETE' }),
  notifications: (id: string, signal?: AbortSignal) => apiFetch<SiteNotifications>(`/sites/${id}/notifications`, { signal }),
  /** Replaces the whole list of extra recipients. */
  saveNotifications: (id: string, recipients: SiteNotificationRecipient[]) => apiFetch<SiteNotifications>(`/sites/${id}/notifications`, { method: 'PUT', body: { recipients } }),
}

export interface SiteFileUpload {
  file: Blob
  /** Required when `file` is a Blob (e.g. a downscaled photo) rather than a File. */
  fileName?: string
  kind: SiteFileKind
  category?: SiteFileCategory
  caption?: string
  width?: number
  height?: number
}

export const LIVE_POLL_MS = 30_000

export const siteQueries = {
  list: (p: { active?: boolean } = {}) =>
    queryOptions({ queryKey: qk.sites.list(p), queryFn: ({ signal }) => sitesApi.list(p, signal), staleTime: 60_000 }),
  detail: (id: string) => queryOptions({ queryKey: qk.sites.detail(id), queryFn: ({ signal }) => sitesApi.get(id, signal), staleTime: 60_000 }),
  live: (id: string) =>
    queryOptions({
      queryKey: qk.sites.live(id),
      queryFn: ({ signal }) => sitesApi.live(id, signal),
      refetchInterval: LIVE_POLL_MS,
      refetchIntervalInBackground: false,
      staleTime: 10_000,
    }),
  livePositions: (id: string) =>
    queryOptions({
      queryKey: qk.sites.livePositions(id),
      queryFn: ({ signal }) => sitesApi.livePositions(id, signal),
      refetchInterval: LIVE_POLL_MS,
      refetchIntervalInBackground: false,
      staleTime: 10_000,
    }),
  conditions: (id: string) =>
    queryOptions({
      queryKey: qk.sites.conditions(id),
      queryFn: ({ signal }) => sitesApi.conditions(id, signal),
      refetchInterval: 60_000,
      refetchIntervalInBackground: false,
      staleTime: 30_000,
    }),
  history: (id: string, p: RangeParams) =>
    queryOptions({
      queryKey: qk.sites.history(id, p),
      queryFn: ({ signal }) => sitesApi.history(id, p, signal),
      staleTime: 30_000,
      placeholderData: keepPreviousData,
    }),
}

export const siteFileQueries = {
  list: (id: string, kind: SiteFileKind) =>
    queryOptions({ queryKey: qk.sites.files(id, kind), queryFn: ({ signal }) => sitesApi.files(id, kind, signal), staleTime: 30_000 }),
  /** A gallery photo as an object URL: photos never change, so it's fetched once per session. */
  photo: (id: string, fileId: string) =>
    queryOptions({
      queryKey: qk.sites.photo(id, fileId),
      queryFn: async ({ signal }) => URL.createObjectURL(await sitesApi.downloadFile(id, fileId, signal)),
      staleTime: Infinity,
      gcTime: 10 * 60_000,
    }),
}
export function useUploadSiteFile(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (u: SiteFileUpload) => sitesApi.uploadFile(id, u),
    onSuccess: (f) => void qc.invalidateQueries({ queryKey: qk.sites.files(id, f.kind) }),
  })
}
export function useUpdateSiteFileCaption(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { fileId: string; caption: string; category?: SiteFileCategory }) => sitesApi.updateCaption(id, v.fileId, v.caption, v.category),
    onSuccess: (f) => qc.setQueryData<SiteFile[]>(qk.sites.files(id, f.kind), (old) => old?.map((x) => (x.id === f.id ? f : x))),
  })
}
export function useDeleteSiteFile(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (f: SiteFile) => sitesApi.deleteFile(id, f.id),
    onSuccess: (_, f) => void qc.invalidateQueries({ queryKey: qk.sites.files(id, f.kind) }),
  })
}

export const siteContactQueries = {
  list: (id: string) => queryOptions({ queryKey: qk.sites.contacts(id), queryFn: ({ signal }) => sitesApi.contacts(id, signal), staleTime: 30_000 }),
}
export function useSaveSiteContact(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { contactId: string | null; body: SiteContactRequest }) =>
      v.contactId ? sitesApi.updateContact(id, v.contactId, v.body) : sitesApi.createContact(id, v.body),
    // Making one contact primary demotes another, and email changes decide who can be notified.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sites.contacts(id) })
      void qc.invalidateQueries({ queryKey: qk.sites.notifications(id) })
    },
  })
}
export function useDeleteSiteContact(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) => sitesApi.deleteContact(id, contactId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sites.contacts(id) })
      void qc.invalidateQueries({ queryKey: qk.sites.notifications(id) })
    },
  })
}

export const siteNotificationQueries = {
  get: (id: string) => queryOptions({ queryKey: qk.sites.notifications(id), queryFn: ({ signal }) => sitesApi.notifications(id, signal), staleTime: 30_000 }),
}
export function useSaveSiteNotifications(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (recipients: SiteNotificationRecipient[]) => sitesApi.saveNotifications(id, recipients),
    onSuccess: (data) => qc.setQueryData(qk.sites.notifications(id), data),
  })
}

export function useCreateSite() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: sitesApi.create, onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.all }) })
}
export function useUpdateSite(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: SiteRequest) => sitesApi.update(id, b),
    onSuccess: (site) => {
      qc.setQueryData(qk.sites.detail(id), site)
      void qc.invalidateQueries({ queryKey: qk.sites.all })
    },
  })
}
export function useDeleteSite() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: sitesApi.remove, onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.all }) })
}
